import { ArrowRight, Linkedin, type LucideIcon, Unplug } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/shadcn/utils';
import { Button } from '@/modules/ui/components/button/Button';

interface GroupNoticeProps {
  tone: 'failed' | 'drafts';
  icon: LucideIcon;
  title: string;
  body: string;
  action: ReactNode;
}

/**
 * A group-wide action right under a stage's header, in the reading path,
 * with the count in its button.
 */
export function GroupNotice({
  tone,
  icon: Icon,
  title,
  body,
  action,
}: GroupNoticeProps) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-x-2.5 gap-y-2 border-t px-3 py-2.5 text-[13px]',
        tone === 'failed' ? 'bg-red-50' : 'bg-secondary-150',
      )}
    >
      <Icon
        aria-hidden
        className={cn(
          'size-4 shrink-0',
          tone === 'failed' ? 'text-red-700' : 'text-primary',
        )}
      />
      <p className="min-w-[min(100%,16rem)] flex-1">
        <span className="font-semibold">{title}</span>{' '}
        <span className="text-neutral-600">{body}</span>
      </p>
      <div className="ml-auto flex shrink-0">{action}</div>
    </div>
  );
}

interface SessionExpiredBannerProps {
  /**
   * Messages that failed because of the session. The API puts them back in
   * the queue when LinkedIn reconnects (finlay-api #710, up to 14 days old).
   */
  waitingCount: number;
  /** Where LinkedIn gets connected; absent when this workspace cannot. */
  integrationsHref?: string;
}

/**
 * The customer's LinkedIn session expired. It blocks every send, so it sits
 * above the table for the whole account, not as a red row in one group.
 */
export function SessionExpiredBanner({
  waitingCount,
  integrationsHref,
}: SessionExpiredBannerProps) {
  const waiting =
    waitingCount === 0
      ? ''
      : waitingCount === 1
        ? ' 1 message is waiting. Nothing is lost; it goes out once you reconnect.'
        : ` ${waitingCount} messages are waiting. Nothing is lost; they go out once you reconnect.`;
  return (
    <LinkedInBanner
      action="Reconnect LinkedIn"
      body={`Finlay can’t send anything until you sign in again.${waiting}`}
      icon={Unplug}
      integrationsHref={integrationsHref}
      title="Your LinkedIn session expired"
      tone="failed"
    />
  );
}

interface ConnectLinkedInBannerProps {
  /** Where LinkedIn gets connected; absent when this workspace cannot. */
  integrationsHref?: string;
}

/**
 * LinkedIn was never connected. Drafting is blocked until it is, so the page
 * says so up front instead of only once people are ticked.
 */
export function ConnectLinkedInBanner({
  integrationsHref,
}: ConnectLinkedInBannerProps) {
  return (
    <LinkedInBanner
      action="Connect LinkedIn"
      body={
        integrationsHref
          ? 'Finlay drafts each message and sends it from your LinkedIn once you approve it.'
          : 'This workspace cannot connect your LinkedIn account. Switch workspaces or ask your recruiter for help.'
      }
      icon={Linkedin}
      integrationsHref={integrationsHref}
      title="Connect LinkedIn to start outreach"
      tone="setup"
    />
  );
}

function LinkedInBanner({
  tone,
  icon: Icon,
  title,
  body,
  action,
  integrationsHref,
}: {
  tone: 'failed' | 'setup';
  icon: LucideIcon;
  title: string;
  body: string;
  action: string;
  integrationsHref?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-[10px] px-4 py-3.5 sm:flex-row sm:items-center sm:gap-3.5',
        tone === 'failed' ? 'bg-red-50' : 'bg-secondary-150',
      )}
    >
      <div className="flex min-w-0 flex-1 items-start gap-3.5 sm:items-center">
        <span
          className={cn(
            'bg-background grid size-8 shrink-0 place-items-center rounded-lg',
            tone === 'failed' ? 'text-red-700' : 'text-primary',
          )}
        >
          <Icon aria-hidden className="size-4" />
        </span>
        <div className="flex min-w-0 flex-col gap-0.5">
          <p className="text-sm font-semibold">{title}</p>
          <p className="text-[13px] text-neutral-600">{body}</p>
        </div>
      </div>
      {integrationsHref && (
        <Button asChild className="h-8 self-end rounded-md sm:self-auto">
          <Link to={integrationsHref}>
            {action}
            <ArrowRight className="size-3.5" />
          </Link>
        </Button>
      )}
    </div>
  );
}
